/**
 * +atmos: far LOD for the open zone. Past the streamed props' draw distance every tree, big rock /
 * cliff and building is still drawn as a tiny low-poly stand-in (a 10-triangle spruce, a canopy
 * blob, a faceted rock, a box house), merged per 384 yd cell (one draw per cell in view). Each
 * stand-in shrinks away inside `near` (the real prop is drawn there) and fades out at the quality
 * preset's `far`, so the whole valley reads from a high point instead of ending at ~170 yd.
 *
 * It only reads `OutPlacement`s (ids, sizes, tints); prop and tree geometry stay the world-detail
 * code's. New tree kinds are picked up by id: `spruce* | fir* | pine*` → conifer, any other
 * `tree*` → round canopy. Presentation only.
 */
import * as THREE from 'three';
import { ownedToonMaterial } from '../../style';
import type { OutPlacement } from '../layout';

/** Real (streamed) props are drawn out to here; stand-ins take over from this distance. */
export const LOD_NEAR = 160;
/** The detailed instanced far forest (field.ts) is drawn out to here; its stand-ins take over. */
export const FAR_TREE_DRAW = 300;

/** A tree of the field's instanced far forest (layout `far`). */
export interface FarTreeIn {
  x: number;
  y: number;
  base: number;
  scale: number;
  pine: boolean;
  yaw: number;
  tint: readonly number[] | null;
}
const CELL = 384;

const CONIFER = /^(spruce|fir|pine)/;
const TREE = /^tree/;
const CLIFF = /^cliff_/;
const ROCK = /^(mossrock_big|boulder|rock_big|scree|spoil_heap)/;
const NATURE = /^(spruce|fir|tree|pine|bush|shrub|fern|mossrock|rock|boulder|cliff_|bramble|stump|log|scree|spoil_heap|mushrooms|flowers|bluebells|violets|barley|reeds)/;

type Tpl = { pos: Float32Array; nor: Float32Array; col: Float32Array };

function tpl(geo: THREE.BufferGeometry, colorAt: (x: number, y: number, z: number) => [number, number, number]): Tpl {
  const g = geo.index ? geo.toNonIndexed() : geo;
  g.computeVertexNormals();
  const p = g.attributes['position'] as THREE.BufferAttribute;
  const n = g.attributes['normal'] as THREE.BufferAttribute;
  const col = new Float32Array(p.count * 3);
  for (let i = 0; i < p.count; i++) col.set(colorAt(p.getX(i), p.getY(i), p.getZ(i)), i * 3);
  const out = { pos: new Float32Array(p.array as ArrayLike<number>), nor: new Float32Array(n.array as ArrayLike<number>), col };
  g.dispose();
  if (g !== geo) geo.dispose();
  return out;
}

const lin = (hex: number): [number, number, number] => {
  const c = new THREE.Color(hex);
  return [c.r, c.g, c.b];
};

/** Unit-height stand-ins (base at y = 0). */
function templates(leaf: number): { conifer: Tpl; round: Tpl; rock: Tpl; cliff: Tpl; house: Tpl } {
  const lc = lin(leaf);
  const shade = (c: [number, number, number], k: number): [number, number, number] => [c[0] * k, c[1] * k, c[2] * k];
  // Conifer: two open 5-sided cones (10 triangles), darker low, lighter crown.
  const c1 = new THREE.ConeGeometry(0.36, 0.62, 5, 1, true).translate(0, 0.08 + 0.31, 0);
  const c2 = new THREE.ConeGeometry(0.25, 0.5, 5, 1, true).translate(0, 0.5 + 0.25, 0);
  const conifer = mergeTpl([tpl(c1, (_x, y) => shade(lc, 0.78 + 0.35 * y)), tpl(c2, (_x, y) => shade(lc, 0.85 + 0.35 * y))]);
  // Round canopy: a low-poly blob on a short trunk.
  const leafR = lin(0x4d8a3e);
  const blob = new THREE.IcosahedronGeometry(0.34, 0).scale(1, 0.85, 1).translate(0, 0.62, 0);
  const trunk = new THREE.CylinderGeometry(0.04, 0.06, 0.34, 4, 1, true).translate(0, 0.17, 0);
  const round = mergeTpl([tpl(blob, (_x, y) => shade(leafR, 0.72 + 0.5 * (y - 0.3))), tpl(trunk, () => lin(0x5a4636))]);
  // Rock / cliff: a faceted squashed icosahedron (unit height, unit radius).
  const rk = lin(0x7c8698);
  const rock = tpl(new THREE.IcosahedronGeometry(1, 0).scale(1, 0.55, 1).translate(0, 0.45, 0), (_x, y) => shade(rk, 0.8 + 0.35 * y));
  // Cliff spire / shelf: an angular tapered 5-sided prism, darker at the foot, lit lip.
  const cliff = tpl(new THREE.CylinderGeometry(0.62, 1, 1, 5, 1, false).translate(0, 0.5, 0), (_x, y) => shade(rk, 0.72 + 0.4 * y));
  // House: box walls + a gable roof (unit box, roof to y = 1).
  const wall = new THREE.BoxGeometry(1, 0.55, 1).translate(0, 0.275, 0);
  const roof = new THREE.CylinderGeometry(0.62, 0.62, 1.05, 3, 1, false).rotateZ(Math.PI / 2).rotateX(-Math.PI / 2).scale(1, 0.5, 1).translate(0, 0.705, 0);
  const house = mergeTpl([tpl(wall, () => lin(0xd9cdb6)), tpl(roof, () => lin(0x6e4f62))]);
  return { conifer, round, rock, cliff, house };
}

function mergeTpl(list: Tpl[]): Tpl {
  const n = list.reduce((a, t) => a + t.pos.length, 0);
  const out: Tpl = { pos: new Float32Array(n), nor: new Float32Array(n), col: new Float32Array(n) };
  let o = 0;
  for (const t of list) {
    out.pos.set(t.pos, o);
    out.nor.set(t.nor, o);
    out.col.set(t.col, o);
    o += t.pos.length;
  }
  return out;
}

export interface FarLod {
  group: THREE.Group;
  count: number;
  /** Quality: how far stand-ins are drawn (yd). */
  setFar(yd: number): void;
  dispose(): void;
}

type Item = { t: Tpl; x: number; y: number; z: number; sx: number; sy: number; sz: number; yaw: number; tint: readonly number[]; near: number };

/** `y` of a placement is its base height (absolute). */
export function buildFarLod(placements: readonly OutPlacement[], leaf: number, farTrees: readonly FarTreeIn[] = []): FarLod {
  const T = templates(leaf);
  const cells = new Map<string, Item[]>();
  let count = 0;
  const add = (sx: number, sy: number, it: Item): void => {
    const key = `${Math.floor(sx / CELL)}|${Math.floor(sy / CELL)}`;
    const l = cells.get(key);
    if (l) l.push(it);
    else cells.set(key, [it]);
    count++;
  };
  for (const p of placements) {
    if (p.layer !== 'prop' && p.layer !== 'detail') continue;
    let it: Item | null = null;
    const base = { x: p.pos.x, y: p.y, z: -p.pos.y, yaw: p.yaw, tint: p.tint, near: LOD_NEAR };
    if (CONIFER.test(p.id) && p.h > 2) it = { t: T.conifer, ...base, sx: p.h, sy: p.h, sz: p.h };
    else if (TREE.test(p.id) && p.h > 2) it = { t: T.round, ...base, sx: p.h, sy: p.h, sz: p.h };
    else if (CLIFF.test(p.id) && p.h > 1.6) it = { t: T.cliff, ...base, sx: p.r * 0.9, sy: p.h, sz: p.r * 0.9 };
    else if (ROCK.test(p.id) && p.h > 1.6) it = { t: T.rock, ...base, sx: p.r * 1.05, sy: p.h, sz: p.r * 1.05 };
    else if (!NATURE.test(p.id) && p.layer === 'prop' && p.h >= 3.2 && p.r >= 1.8) it = { t: T.house, ...base, sx: p.r * 1.45, sy: p.h, sz: p.r * 1.15 };
    if (!it) continue;
    add(p.pos.x, p.pos.y, it);
  }
  for (const f of farTrees) {
    const h = (f.pine ? 10 : 8) * f.scale;
    add(f.x, f.y, { t: f.pine ? T.conifer : T.round, x: f.x, y: f.base, z: -f.y, sx: h, sy: h, sz: h, yaw: f.yaw, tint: f.tint ?? [1, 1, 1], near: FAR_TREE_DRAW });
  }
  const uLod = { value: new THREE.Vector2(LOD_NEAR, 1600) };
  const mat = ownedToonMaterial({ color: 0xffffff, vertexColors: true });
  mat.name = 'field-far-lod';
  const base = mat.onBeforeCompile;
  mat.onBeforeCompile = (sh, r) => {
    base.call(mat, sh, r);
    sh.uniforms['uLod'] = uLod;
    sh.vertexShader = sh.vertexShader.replace('#include <common>', '#include <common>\nattribute vec4 aCenter; uniform vec2 uLod;').replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      {
        // Shrink away inside the real props' range and out at the quality preset's far distance.
        float dc = distance(aCenter.xyz, cameraPosition);
        float k = smoothstep(aCenter.w - 6.0, aCenter.w + 4.0, dc) * (1.0 - smoothstep(uLod.y - 90.0, uLod.y, dc));
        transformed = aCenter.xyz + (transformed - aCenter.xyz) * k;
      }`,
    );
  };
  mat.customProgramCacheKey = () => 'toon-far-lod';
  const group = new THREE.Group();
  group.name = 'far-lod';
  const geos: THREE.BufferGeometry[] = [];
  const drop = function (this: THREE.BufferAttribute): void {
    (this as unknown as { array: ArrayLike<number> }).array = new Float32Array(0);
  };
  for (const list of cells.values()) {
    const n = list.reduce((a, it) => a + it.t.pos.length, 0);
    const pos = new Float32Array(n);
    const nor = new Float32Array(n);
    const col = new Float32Array(n);
    const cen = new Float32Array((n / 3) * 4);
    let o = 0;
    for (const it of list) {
      const c = Math.cos(it.yaw);
      const s = Math.sin(it.yaw);
      const tp = it.t;
      const tr = it.tint[0] ?? 1;
      const tg = it.tint[1] ?? 1;
      const tb = it.tint[2] ?? 1;
      for (let i = 0; i < tp.pos.length; i += 3) {
        const lx = tp.pos[i]! * it.sx;
        const ly = tp.pos[i + 1]! * it.sy;
        const lz = tp.pos[i + 2]! * it.sz;
        pos[o + i] = it.x + lx * c + lz * s;
        pos[o + i + 1] = it.y + ly;
        pos[o + i + 2] = it.z - lx * s + lz * c;
        const nx = tp.nor[i]!;
        const nz = tp.nor[i + 2]!;
        nor[o + i] = nx * c + nz * s;
        nor[o + i + 1] = tp.nor[i + 1]!;
        nor[o + i + 2] = -nx * s + nz * c;
        col[o + i] = tp.col[i]! * tr;
        col[o + i + 1] = tp.col[i + 1]! * tg;
        col[o + i + 2] = tp.col[i + 2]! * tb;
        const q = ((o + i) / 3) * 4;
        cen[q] = it.x;
        cen[q + 1] = it.y + it.sy * 0.4;
        cen[q + 2] = it.z;
        cen[q + 3] = it.near;
      }
      o += tp.pos.length;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    geo.setAttribute('aCenter', new THREE.BufferAttribute(cen, 4));
    geo.computeBoundingSphere();
    for (const k of Object.keys(geo.attributes)) (geo.attributes[k] as THREE.BufferAttribute).onUpload(drop);
    const m = new THREE.Mesh(geo, mat);
    m.name = 'far-lod-cell';
    m.userData['noStyle'] = true;
    m.castShadow = false;
    m.receiveShadow = false;
    group.add(m);
    geos.push(geo);
  }
  return {
    group,
    count,
    setFar(yd) {
      uLod.value.y = yd;
      // Whole cells past the far distance are skipped.
      for (const m of group.children) m.visible = yd > LOD_NEAR + 20;
    },
    dispose() {
      group.removeFromParent();
      for (const g of geos) g.dispose();
      mat.dispose();
    },
  };
}

/** A stand-alone conifer stand-in (`height` yd tall, vertex colours) for other far layers. */
export function coniferStandIn(leaf: number, height: number): THREE.BufferGeometry {
  const t = templates(leaf).conifer;
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(t.pos.map((v) => v * height), 3));
  g.setAttribute('normal', new THREE.BufferAttribute(t.nor, 3));
  g.setAttribute('color', new THREE.BufferAttribute(t.col, 3));
  return g;
}
